import type { TFunction } from "i18next";
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from "@divespots/contracts";
import type { AuthFailed } from "../../auth/auth-api.ts";

/**
 * Words one failure for the visitor. Unknown email and wrong password are ONE message (`invalid`);
 * nothing here says whether an address has an account.
 */
export function failureText(t: TFunction, f: AuthFailed): string {
  switch (f.reason) {
    case "invalid":
      return t("diver.auth.err.invalid");
    case "email_not_verified":
      return t("diver.auth.err.notVerified");
    case "banned":
      return t("diver.auth.err.banned");
    case "rate_limited": {
      const s = f.retryAfterSec;
      if (s === undefined) return t("diver.auth.err.rateLimited");
      return s > 90
        ? t("diver.auth.err.rateLimitedMin", { count: Math.ceil(s / 60) })
        : t("diver.auth.err.rateLimitedSec", { count: s });
    }
    case "invalid_token":
      return t("diver.auth.err.invalidToken");
    case "network":
      return t("diver.auth.err.network");
    default:
      return t("diver.auth.err.failed");
  }
}

export type LengthState = "empty" | "short" | "ok" | "long";

/** Live length check for a new password; informational, never blocks typing. */
export function lengthState(pw: string): { state: LengthState; missing: number } {
  if (pw.length === 0) return { state: "empty", missing: PASSWORD_MIN_LENGTH };
  if (pw.length < PASSWORD_MIN_LENGTH) {
    return { state: "short", missing: PASSWORD_MIN_LENGTH - pw.length };
  }
  if (pw.length > PASSWORD_MAX_LENGTH) return { state: "long", missing: 0 };
  return { state: "ok", missing: 0 };
}

export const looksLikeEmail = (v: string): boolean => /^\S+@\S+\.\S+$/.test(v.trim());
